import { Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Chip, MODULES, TONES } from "@/components/common";

export const ALLERGEN_LABELS = { gluten: "Gluten", lactosa: "Lactosa", huevo: "Huevo", frutos_secos: "Frutos secos", pescado: "Pescado", marisco: "Marisco", soja: "Soja" };
const ACTIVITY = [["sedentario", "Sedentario"], ["ligero", "Ligero"], ["moderado", "Moderado"], ["alto", "Muy activo"]];
const DIETS = [["omnivora", "Omnívora"], ["pescetariana", "Pescetariana"], ["vegetariana", "Vegetariana"], ["vegana", "Vegana"]];
const GOAL_KINDS = [
  ["carrera", "Carrera / distancia"],
  ["competicion", "Competición / partido"],
  ["rendimiento", "Marca / rendimiento"],
  ["habito", "Hábito"],
  ["otro", "Otro"],
];
const GOAL_EXAMPLES = [
  { title: "Media maratón", kind: "carrera", target: "1:45:00", notes: "" },
  { title: "Maratón", kind: "carrera", target: "terminar", notes: "" },
  { title: "Velocidad máxima", kind: "rendimiento", target: "30 km/h", notes: "" },
  { title: "Fútbol — liga local", kind: "competicion", target: "", notes: "" },
];

const newGoal = () => ({
  id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  title: "",
  kind: "otro",
  target: "",
  target_date: "",
  notes: "",
});

export const Field = ({ label, children, hint }) => (
  <div>
    <label className="mb-2 block text-sm font-semibold text-ink">{label}</label>
    {children}
    {hint && <p className="mt-1.5 text-xs text-stone-500">{hint}</p>}
  </div>
);

const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

export const BasicsSection = ({ f, set }) => (
  <div className="grid gap-5 sm:grid-cols-2">
    <Field label="Nombre"><Input value={f.name} onChange={(e) => set({ name: e.target.value })} className="h-12 rounded-2xl" data-testid="profile-name-input" /></Field>
    <Field label="Fecha de nacimiento"><Input type="date" value={f.birth_date} onChange={(e) => set({ birth_date: e.target.value })} className="h-12 rounded-2xl" data-testid="profile-birthdate-input" /></Field>
    <Field label="Altura (cm)"><Input type="number" inputMode="decimal" value={f.height_cm} onChange={(e) => set({ height_cm: e.target.value })} className="h-12 rounded-2xl" data-testid="profile-height-input" /></Field>
    <Field label="Peso (kg)"><Input type="number" inputMode="decimal" step="0.1" value={f.weight_kg} onChange={(e) => set({ weight_kg: e.target.value })} className="h-12 rounded-2xl" data-testid="profile-weight-input" /></Field>
    <Field label="Sexo">
      <div className="flex flex-wrap gap-2">
        {[["femenino", "Mujer"], ["masculino", "Hombre"], ["otro", "Prefiero no decirlo"]].map(([v, l]) => (
          <Chip key={v} active={f.gender === v} onClick={() => set({ gender: v })} testid={`gender-${v}`}>{l}</Chip>
        ))}
      </div>
    </Field>
    <Field label="Actividad diaria (sin contar entrenos)">
      <div className="flex flex-wrap gap-2">
        {ACTIVITY.map(([v, l]) => <Chip key={v} active={f.activity_level === v} onClick={() => set({ activity_level: v })} testid={`activity-${v}`}>{l}</Chip>)}
      </div>
    </Field>
  </div>
);

export const GoalsSection = ({ f, set, options }) => {
  const goals = f.specific_goals || [];
  const setGoals = (next) => set({ specific_goals: next });
  const updateGoal = (id, patch) => setGoals(goals.map((g) => (g.id === id ? { ...g, ...patch } : g)));
  const removeGoal = (id) => setGoals(goals.filter((g) => g.id !== id));
  const addGoal = (seed = null) => setGoals([...goals, { ...newGoal(), ...(seed || {}) }]);

  return (
    <div className="space-y-6">
      <Field label="¿Qué quieres conseguir? (elige uno o varios)">
        <div className="flex flex-wrap gap-2">
          {options.goals.map((g, i) => <Chip key={g} tone="train" active={f.goals.includes(g)} onClick={() => set({ goals: toggle(f.goals, g) })} testid={`goal-${i}`}>{g}</Chip>)}
        </div>
      </Field>

      <div className="space-y-3" data-testid="specific-goals-section">
        <Field
          label="Objetivos concretos (opcional)"
          hint="Media maratón, una marca de velocidad, un torneo de tenis, la liga de fútbol… El Coach y el plan los tendrán en cuenta."
        >
          <div className="mb-3 flex flex-wrap gap-2">
            {GOAL_EXAMPLES.map((ex) => (
              <button
                key={ex.title}
                type="button"
                onClick={() => addGoal(ex)}
                data-testid={`specific-goal-example-${ex.kind}`}
                className="rounded-full border border-dashed border-train/40 bg-train-soft/50 px-3 py-1.5 text-xs font-semibold text-train-ink hover:bg-train-soft"
              >
                + {ex.title}
              </button>
            ))}
          </div>
        </Field>
        {goals.map((g, i) => (
          <div key={g.id || i} className="space-y-3 rounded-2xl border border-stone-200 bg-white p-4" data-testid={`specific-goal-${i}`}>
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs font-bold uppercase tracking-wide text-train-ink">Objetivo {i + 1}</p>
              <button type="button" onClick={() => removeGoal(g.id)} data-testid={`specific-goal-remove-${i}`} className="rounded-full p-1.5 text-stone-400 hover:bg-stone-100 hover:text-ink">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <Input
              value={g.title}
              onChange={(e) => updateGoal(g.id, { title: e.target.value })}
              placeholder="Ej.: correr media maratón / liga de baloncesto"
              className="h-11 rounded-2xl"
              data-testid={`specific-goal-title-${i}`}
            />
            <div className="flex flex-wrap gap-2">
              {GOAL_KINDS.map(([v, l]) => (
                <Chip key={v} tone="train" active={g.kind === v} onClick={() => updateGoal(g.id, { kind: v })} testid={`specific-goal-kind-${i}-${v}`}>{l}</Chip>
              ))}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Meta / marca">
                <Input
                  value={g.target || ""}
                  onChange={(e) => updateGoal(g.id, { target: e.target.value })}
                  placeholder="1:45:00 · 42 km · 30 km/h…"
                  className="h-11 rounded-2xl"
                  data-testid={`specific-goal-target-${i}`}
                />
              </Field>
              <Field label="Fecha objetivo">
                <Input
                  type="date"
                  value={g.target_date || ""}
                  onChange={(e) => updateGoal(g.id, { target_date: e.target.value })}
                  className="h-11 rounded-2xl"
                  data-testid={`specific-goal-date-${i}`}
                />
              </Field>
            </div>
            <Field label="Notas">
              <Textarea
                value={g.notes || ""}
                onChange={(e) => updateGoal(g.id, { notes: e.target.value })}
                placeholder="Contexto: nivel, competición, restricciones…"
                className="rounded-2xl"
                rows={2}
                data-testid={`specific-goal-notes-${i}`}
              />
            </Field>
          </div>
        ))}
        <button
          type="button"
          onClick={() => addGoal()}
          data-testid="specific-goal-add-btn"
          className="flex items-center gap-2 rounded-full border-2 border-train px-4 py-2.5 text-sm font-semibold text-train-ink transition-transform active:scale-95"
        >
          <Plus className="h-4 w-4" /> Añadir objetivo concreto
        </button>
      </div>

      <Field label="Tu nivel">
        <div className="flex flex-wrap gap-2">
          {["Principiante", "Intermedio", "Avanzado"].map((v) => <Chip key={v} tone="train" active={f.experience === v} onClick={() => set({ experience: v })} testid={`experience-${v.toLowerCase()}`}>{v}</Chip>)}
        </div>
      </Field>
      <Field label="Días de entreno por semana">
        <div className="flex flex-wrap gap-2">
          {[1, 2, 3, 4, 5, 6, 7].map((n) => <Chip key={n} tone="train" active={Number(f.training_days_per_week) === n} onClick={() => set({ training_days_per_week: n })} testid={`days-${n}`}>{n}</Chip>)}
        </div>
      </Field>
      <div className="flex items-center justify-between rounded-2xl bg-train-soft p-4">
        <div>
          <p className="text-sm font-semibold">Tengo acceso a gimnasio</p>
          <p className="text-xs text-stone-600">Si no, te preparamos rutinas en casa.</p>
        </div>
        <Switch checked={f.has_gym} onCheckedChange={(v) => set({ has_gym: v })} data-testid="profile-gym-switch" />
      </div>
      <Field label="Deportes que te gustan">
        <div className="flex flex-wrap gap-2">
          {options.sports.map((s) => <Chip key={s} tone="train" active={f.sports_preferences.includes(s)} onClick={() => set({ sports_preferences: toggle(f.sports_preferences, s) })} testid={`sport-${s.toLowerCase()}`}>{s}</Chip>)}
        </div>
      </Field>
    </div>
  );
};

export const FoodSection = ({ f, set, options }) => (
  <div className="space-y-6">
    <Field label="Tipo de alimentación">
      <div className="flex flex-wrap gap-2">
        {DIETS.map(([v, l]) => <Chip key={v} tone="food" active={f.diet_type === v} onClick={() => set({ diet_type: v })} testid={`diet-${v}`}>{l}</Chip>)}
      </div>
    </Field>
    <Field label="Alergias e intolerancias">
      <div className="flex flex-wrap gap-2">
        {options.allergens.map((a) => <Chip key={a} tone="food" active={f.allergies.includes(a)} onClick={() => set({ allergies: toggle(f.allergies, a) })} testid={`allergy-${a}`}>{ALLERGEN_LABELS[a]}</Chip>)}
      </div>
    </Field>
    <Field label="Alimentos que no te gustan" hint="Sepáralos por comas. Ej.: brócoli, atún">
      <Input value={f.dislikes} onChange={(e) => set({ dislikes: e.target.value })} className="h-12 rounded-2xl" data-testid="profile-dislikes-input" />
    </Field>
    <Field label="Suplementos que tomas (opcional)">
      <div className="flex flex-wrap gap-2">
        {options.supplements.map((s, i) => <Chip key={s} tone="food" active={f.supplements.includes(s)} onClick={() => set({ supplements: toggle(f.supplements, s) })} testid={`supplement-${i}`}>{s}</Chip>)}
      </div>
    </Field>
  </div>
);

export const ModulesSection = ({ modules, onChange }) => (
  <div className="grid gap-3">
    {Object.entries(MODULES).map(([k, m]) => (
      <div role="button" tabIndex={0} key={k} onClick={() => onChange({ ...modules, [k]: !modules[k] })} onKeyDown={(e) => e.key === "Enter" && onChange({ ...modules, [k]: !modules[k] })} data-testid={`module-toggle-${k}`}
        className={`flex cursor-pointer items-center gap-4 rounded-3xl border-2 p-4 text-left transition-colors ${modules[k] ? `${TONES[m.tone].border} ${TONES[m.tone].soft}` : "border-stone-200 bg-white"}`}>
        <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${modules[k] ? TONES[m.tone].solid : "bg-stone-100 text-stone-500"}`}><m.icon className="h-6 w-6" /></span>
        <span className="flex-1">
          <span className="block font-display font-bold">{m.label}</span>
          <span className="block text-sm text-stone-600">{m.desc}</span>
        </span>
        <span aria-hidden className={`flex h-6 w-11 shrink-0 items-center rounded-full p-0.5 transition-colors ${modules[k] ? "bg-ink" : "bg-stone-200"}`}><span className={`h-5 w-5 rounded-full bg-white shadow transition-transform ${modules[k] ? "translate-x-5" : ""}`} /></span>
      </div>
    ))}
  </div>
);

export const NotesSection = ({ f, set }) => (
  <Field label="Notas médicas (opcional)" hint="Medicación, operaciones, embarazo… Solo para tener en cuenta.">
    <Textarea value={f.medical_notes} onChange={(e) => set({ medical_notes: e.target.value })} className="rounded-2xl" data-testid="profile-medical-notes" />
  </Field>
);

export const toPayload = (f) => ({
  ...f,
  height_cm: Number(f.height_cm),
  weight_kg: Number(f.weight_kg),
  training_days_per_week: Number(f.training_days_per_week),
  specific_goals: (f.specific_goals || [])
    .filter((g) => (g.title || "").trim())
    .map((g) => ({
      id: g.id?.startsWith?.("tmp-") ? undefined : g.id,
      title: g.title.trim(),
      kind: g.kind || "otro",
      target: (g.target || "").trim(),
      target_date: g.target_date || null,
      notes: (g.notes || "").trim(),
    })),
});
